import * as React from 'react';

import { enumLoadStatus, LoadStatus } from '@rematter/frontend-lib-api-client';
import {
  loadStatusColors,
  loadStatusTextMap,
} from '@rematter/modules/trade/utils/status';
import { MultiSelectFilter } from '@rematter/ui-web';
import { cn } from '@rematter/ui-web/lib';
import { useAtom } from 'jotai';

import { $columnSelectedStatuses } from '../../../_state/loadsList.state';

import { useTranslations } from '~/modules/trade/i18n/useTranslations';

interface StatusOption {
  id: LoadStatus;
  label: string;
}

interface StatusColumnFilterProps {
  onClose?: () => void;
}

export const StatusColumnFilter: React.FC<StatusColumnFilterProps> = ({
  onClose,
}) => {
  const t = useTranslations();
  const [selectedStatusesSet, setSelectedStatusesSet] = useAtom(
    $columnSelectedStatuses,
  );
  const [searchQuery, setSearchQuery] = React.useState('');
  const selectedStatuses = Array.from(selectedStatusesSet);

  // Convert string array to Set for MultiSelectFilter
  const selectedKeys = React.useMemo(
    () => new Set<React.Key>(selectedStatuses),
    [selectedStatuses],
  );

  // Define all available status options from the enum for maintainability
  const statusOptions: StatusOption[] = React.useMemo(
    () =>
      Object.values(enumLoadStatus).map((status) => ({
        id: status,
        label: loadStatusTextMap[status](t, true),
      })),

    [],
  );

  // Filter status options based on search query
  const filteredStatusOptions = React.useMemo(
    () =>
      statusOptions.filter((option) =>
        option.label.toLowerCase().includes(searchQuery.toLowerCase()),
      ),
    [statusOptions, searchQuery],
  );

  // Handle selection changes
  const handleSelectionChange = React.useCallback(
    (keys: Set<React.Key>) => {
      setSelectedStatusesSet(new Set(Array.from(keys) as LoadStatus[]));
    },
    [setSelectedStatusesSet],
  );

  // Handle reset
  const handleReset = React.useCallback(() => {
    setSelectedStatusesSet(new Set());
  }, [setSelectedStatusesSet]);

  // Handle search query changes for client-side filtering
  const handleSearch = React.useCallback((query: string) => {
    setSearchQuery(query);
  }, []);

  // Custom item renderer for statuses
  const getLabel = React.useCallback(
    (item: StatusOption, isSelected: boolean) => (
      <div className="flex w-full items-center gap-2">
        <div
          className={cn(
            'w-0 grow truncate text-sm',
            isSelected ? 'text-neutral' : 'text-neutral/60',
          )}
        >
          {item.label}
        </div>
        <span
          className={cn('size-2 rounded-full', loadStatusColors[item.id])}
        />
      </div>
    ),
    [],
  );

  // Get selected status options for display
  const selectedStatusOptions = React.useMemo(
    () =>
      statusOptions.filter((option) => selectedStatuses.includes(option.id)),
    [statusOptions, selectedStatuses],
  );

  return (
    <MultiSelectFilter
      className="@StatusColumnFilter min-w-[250px]"
      entries={filteredStatusOptions}
      selectedEntries={selectedStatusOptions}
      selectedKeys={selectedKeys}
      onSelectionChange={handleSelectionChange}
      getEntryKey={(status) => status.id}
      getEntryLabel={getLabel}
      onSearch={handleSearch}
      onDismiss={onClose}
      onReset={handleReset}
      searchPlaceholder="Search statuses..."
      isLoading={false}
      isFetching={false}
    />
  );
};
